import { useState, useCallback, useEffect } from 'react';

import { supabase } from '../../../lib/supabase';
import type { Player } from '../../../types/multiplayer';

interface UseRoomPlayersOptions {
  roomId: string;
  userId: string | null;
}

export function useRoomPlayers({ roomId, userId }: UseRoomPlayersOptions) {
  const [players, setPlayers] = useState<Player[]>([]);
  const [isReady, setIsReady] = useState(false);

  // Fetch and format players from the database
  const syncPlayers = useCallback(async () => {
    console.log('[syncPlayers] called with roomId:', roomId, 'userId:', userId);
    if (!roomId) return;
    try {
      const { data: playersData, error } = await supabase
        .from('multiplayer_game_room_players')
        .select('*, user_profiles(name, avatar_url, language, streak)')
        .eq('room_id', roomId);
      if (error) {
        console.error('Error fetching players:', error);
        return;
      }
      if (playersData && playersData.length > 0) {
        const formattedPlayers = playersData.map((player: any) => {
          const profile = player.user_profiles;
          if (__DEV__) {
            console.log('[useRoomPlayers] Processing player', player.user_id, {
              player_name: player.name,
              profile_exists: !!profile,
              profile_name: profile?.name,
              final_name_will_be: profile?.name || player.name || player.nickname || 'Player',
            });
          }
          return {
            id: player.user_id,
            user_id: player.user_id,
            name: profile?.name || player.name || player.nickname || 'Player',
            avatar_url: profile?.avatar_url || player.avatar_url || '',
            is_ready: Boolean(player.is_ready),
            is_host: Boolean(player.is_host),
            status: 'online' as const,
            joined_at: player.joined_at || new Date().toISOString(),
            last_seen: new Date().toISOString(),
          };
        });
        setPlayers(formattedPlayers);
        // Check if current user is ready
        if (userId) {
          const currentPlayer = playersData.find((p: any) => p.user_id === userId);
          if (currentPlayer) {
            setIsReady(Boolean(currentPlayer.is_ready));
          }
        }
      }
    } catch (error) {
      console.error('Error syncing players:', error);
    }
  }, [roomId, userId]);

  // Ensure player record exists in the room
  const ensurePlayerInRoom = useCallback(async (userIdToEnsure: string, roomIdToEnsure: string) => {
    try {
      // First check if player already exists
      const { data: existingPlayer } = await supabase
        .from('multiplayer_game_room_players')
        .select('*')
        .eq('user_id', userIdToEnsure)
        .eq('room_id', roomIdToEnsure)
        .single();

      if (existingPlayer) {
        console.log('[ensurePlayerInRoom] Player already exists:', existingPlayer);
        return existingPlayer;
      }

      // Get user profile for name
      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('name, avatar_url')
        .eq('id', userIdToEnsure)
        .single();

      // Create player record
      const { data: newPlayer, error } = await supabase
        .from('multiplayer_game_room_players')
        .insert({
          user_id: userIdToEnsure,
          room_id: roomIdToEnsure,
          name: userProfile?.name || 'Player',
          avatar_url: userProfile?.avatar_url || '',
          is_ready: false,
          is_host: false,
          joined_at: new Date().toISOString()
        })
        .select('*')
        .single();

      if (error) {
        console.error('[ensurePlayerInRoom] Error creating player record:', error);
        throw error;
      }

      console.log('[ensurePlayerInRoom] Created new player record:', newPlayer);
      return newPlayer;
    } catch (error) {
      console.error('[ensurePlayerInRoom] Error:', error);
      throw error;
    }
  }, []);

  // Toggle player ready status with improved error handling
  const toggleReady = useCallback(async () => {
    if (!userId || !roomId) {
      console.warn('[toggleReady] Missing userId or roomId');
      return false;
    }

    try {
      console.log('[toggleReady] Attempting to toggle ready status for user:', userId, 'room:', roomId);
      
      // First ensure the player record exists
      await ensurePlayerInRoom(userId, roomId);
      
      const newReadyStatus = !isReady;
      console.log('[toggleReady] New ready status will be:', newReadyStatus);
      
      // Try to update the ready status
      const { data, error } = await supabase
        .from('multiplayer_game_room_players')
        .update({ 
          is_ready: newReadyStatus,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId)
        .eq('room_id', roomId)
        .select('*')
        .single();

      if (error) {
        console.error('[toggleReady] Error updating ready status:', error);
        
        // If we get a "no rows" error, try to create the player record and retry
        if (error.code === 'PGRST116') {
          console.log('[toggleReady] No rows found, ensuring player exists and retrying...');
          await ensurePlayerInRoom(userId, roomId);
          
          // Retry the update
          const { data: retryData, error: retryError } = await supabase
            .from('multiplayer_game_room_players')
            .update({ 
              is_ready: newReadyStatus,
              updated_at: new Date().toISOString()
            })
            .eq('user_id', userId)
            .eq('room_id', roomId)
            .select('*')
            .single();

          if (retryError) {
            console.error('[toggleReady] Retry failed:', retryError);
            throw retryError;
          }

          if (retryData) {
            console.log('[toggleReady] Retry successful:', retryData);
            setIsReady(retryData.is_ready);
            setPlayers(prev => prev.map(p => 
              p.user_id === userId ? { ...p, is_ready: retryData.is_ready } : p
            ));
            return retryData.is_ready;
          }
        } else {
          throw error;
        }
      }

      if (data) {
        console.log('[toggleReady] Successfully updated ready status:', data);
        setIsReady(data.is_ready);
        setPlayers(prev => prev.map(p => 
          p.user_id === userId ? { ...p, is_ready: data.is_ready } : p
        ));
        return data.is_ready;
      }

      // Fallback - update local state even if database update didn't return data
      console.log('[toggleReady] No data returned, updating local state');
      setIsReady(newReadyStatus);
      setPlayers(prev => prev.map(p => 
        p.user_id === userId ? { ...p, is_ready: newReadyStatus } : p
      ));
      return newReadyStatus;
      
    } catch (error) {
      console.error('[toggleReady] Error toggling ready status:', error);
      throw error;
    }
  }, [userId, roomId, isReady, ensurePlayerInRoom]);

  // Set up real-time subscription for player changes
  useEffect(() => {
    if (!roomId) return;

    console.log('[useRoomPlayers] Setting up real-time subscription for room:', roomId);
    
    // Create a stable sync function for this effect
    const syncPlayersStable = async () => {
      if (!roomId) return;
      try {
        console.log('[useRoomPlayers] Syncing players for room:', roomId);
        const { data: playersData, error } = await supabase
          .from('multiplayer_game_room_players')
          .select('*, user_profiles(name, avatar_url, language, streak)')
          .eq('room_id', roomId);
        
        if (error) {
          console.error('[useRoomPlayers] Error fetching players:', error);
          return;
        }
        
        if (playersData && playersData.length > 0) {
          console.log('[useRoomPlayers] Raw player data:', playersData);
          const formattedPlayers = playersData.map((player: any) => {
            const profile = player.user_profiles;
            const formattedPlayer = {
              id: player.user_id,
              user_id: player.user_id,
              name: profile?.name || player.name || player.nickname || 'Player',
              avatar_url: profile?.avatar_url || player.avatar_url || '',
              is_ready: Boolean(player.is_ready),
              is_host: Boolean(player.is_host),
              status: 'online' as const,
              joined_at: player.joined_at || new Date().toISOString(),
              last_seen: new Date().toISOString(),
            };
            console.log('[useRoomPlayers] Formatted player:', formattedPlayer);
            return formattedPlayer;
          });
          
          console.log('[useRoomPlayers] Setting players:', formattedPlayers);
          setPlayers(formattedPlayers);
          
          // Check if current user is ready
          if (userId) {
            const currentPlayer = playersData.find((p: any) => p.user_id === userId);
            if (currentPlayer) {
              const newReadyState = Boolean(currentPlayer.is_ready);
              console.log('[useRoomPlayers] Current user ready state:', newReadyState);
              setIsReady(newReadyState);
            }
          }
        } else {
          console.log('[useRoomPlayers] No players found for room:', roomId);
        }
      } catch (error) {
        console.error('[useRoomPlayers] Error syncing players:', error);
      }
    };
    
    // Initial sync
    syncPlayersStable();
    
    // Set up periodic sync to ensure we don't miss updates
    const periodicSync = setInterval(() => {
      console.log('[useRoomPlayers] Periodic sync for room:', roomId);
      syncPlayersStable();
    }, 3000); // Sync every 3 seconds

    // Subscribe to player changes with enhanced logging and immediate updates
    const subscription = supabase
      .channel(`room-players-${roomId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'multiplayer_game_room_players',
          filter: `room_id=eq.${roomId}`,
        },
        (payload) => {
          console.log('[useRoomPlayers] Real-time update received:', {
            event: payload.eventType,
            table: payload.table,
            new: payload.new,
            old: payload.old,
            roomId: roomId
          });
          
          // Handle different event types
          if (payload.eventType === 'INSERT') {
            console.log('[useRoomPlayers] New player joined, syncing immediately');
          } else if (payload.eventType === 'UPDATE') {
            console.log('[useRoomPlayers] Player updated, syncing immediately');
          } else if (payload.eventType === 'DELETE') {
            console.log('[useRoomPlayers] Player left, syncing immediately');
          }
          
          // Immediate re-sync when any player data changes
          console.log('[useRoomPlayers] Triggering immediate sync after real-time update');
          syncPlayersStable();
          
          // Also trigger a second sync after a small delay to ensure consistency
          setTimeout(() => {
            console.log('[useRoomPlayers] Triggering delayed sync for consistency');
            syncPlayersStable();
          }, 200);
        }
      )
      .subscribe((status) => {
        console.log('[useRoomPlayers] Subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('[useRoomPlayers] Successfully subscribed to real-time updates for room:', roomId);
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[useRoomPlayers] Channel error for room:', roomId);
        }
      });

    return () => {
      console.log('[useRoomPlayers] Cleaning up subscription for room:', roomId);
      clearInterval(periodicSync);
      subscription.unsubscribe();
    };
  }, [roomId, userId]); // Include userId to handle ready state updates

  return {
    players,
    setPlayers,
    isReady,
    setIsReady,
    syncPlayers,
    toggleReady,
    ensurePlayerInRoom, // Export this for external use if needed
  };
}
